/**
 * ⚠️ EZ A MODUL NEM FUT — ELVETETT MEGKÖZELÍTÉS, dokumentációként megtartva.
 *
 * A lentebbi feltevés ("a Unity per-entry alkalmaz, tehát két indexelt entryből csak az utóbbi
 * érvényesül") TÉVES. A Unity-spec szerint egy stylescript-entry PONTOSAN EGY indexelt kulcsot
 * hordoz, és az entryk indexenként külön mennek ki:
 *     {"TargetType":"FrontZone","Fronts[2]":"Left"}
 *     {"TargetType":"FrontZone","Fronts[0]":"Double"}
 * A fésült, két indexelt kulcsot hordozó entryt a Unity NEM értelmezi — élesben ez azzal járt, hogy a
 * fiókos zóna front-váltása (Dupla / Front nélküli) némán hatástalan volt, hibaüzenet nélkül. Ezért a
 * ParametersProcessor.generateStyleScript NEM hívja ezt a függvényt; ha egyszer mégis indexelt entryket
 * kellene összevonni, előbb a Unity-devvel kell egyeztetni, mert a mai build ezt a formát elutasítja.
 *
 * A modul a történet miatt maradt itt (a V62 ezzel a feltevéssel készült); szabadon törölhető.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * INDEXELT KULCSOK ÖSSZEFÉSÜLÉSE — azonos célra mutató `Név[index]` alakú stylescript-entryk egybe.
 *
 * (EREDETI, TÉVES INDOKLÁS) a Unity a stylescript entryket EGYENKÉNT alkalmazza a cél elemre, és egy
 * indexelt tulajdonság-halmaznál (`Fronts[0]`, `Fronts[2]`, …) a később alkalmazott entry felülcsapja
 * a korábbit.
 * Ha tehát egy terméken KÉT paraméter ugyanannak a tulajdonságnak két különböző indexét állítja
 * (pl. a fiókos zóna frontja `Fronts[0]`, a polcos zóna frontja `Fronts[2]`), akkor két külön entry
 * megy ki ugyanarra a FrontZone-ra, és csak az utolsó érvényesül:
 *
 *     {"Fronts[2]": "Left", "TargetType": "FrontZone"}     ← elveszik
 *     {"Fronts[0]": "Full", "TargetType": "FrontZone"}     ← csak ez érvényesül
 *
 * Ez a függvény egyetlen entry-vé fésüli őket:
 *
 *     {"Fronts[2]": "Left", "Fronts[0]": "Full", "TargetType": "FrontZone"}
 *
 * UGYANAZ A MINTA, mint a mergeRelationsWrappers (backPassingRelations.ts): ott is azért kellett az
 * azonos célra mutató wrappereket EGY wrapperbe hozni, mert a Unity per-entry alkalmaz. Ez a
 * függvény annak a szűkebb, indexelt-kulcsos párja.
 *
 * SZÁNDÉKOSAN SZŰK — csak akkor fésül, ha MINDHÁROM feltétel áll:
 *   1. a wrapperben PONTOSAN EGY nem-cél kulcs van (a `TargetType` / `Path` a cél),
 *   2. ez a kulcs `Név[szám]` alakú,
 *   3. a másik wrapper cél-hármasa ÉS a kulcs BÁZISNEVE is azonos.
 * Így nem fésül össze semmi mást, ami ugyanarra a célra mutat — pl. a FrontZone `Decor`-ja külön entry
 * marad, ahogy eddig is (az nem indexelt kulcs). Két, azonos indexre mutató wrapper sem fordulhat elő,
 * mert a paraméter-sorok (termék + property + index) egyediek; a fésülés tehát sosem veszít adatot.
 *
 * NO-OP A MAI ADATON: egyetlen termékben sincs két indexelt sor ugyanabból a property-ből, ezért
 * minden meglévő termék kimenete bájtra változatlan marad.
 */

/** A wrapper egyetlen `Név[index]` alakú kulcsának bázisneve, ha pontosan egy ilyen kulcsa van. */
const indexedBaseOf = (wrapper: any): string | null => {
    if (!wrapper || typeof wrapper !== "object" || Array.isArray(wrapper)) return null;

    const keys = Object.keys(wrapper).filter((key) => key !== "TargetType" && key !== "Path");
    if (keys.length !== 1) return null;

    const match = keys[0].match(/^(\w+)\[\d+\]$/);
    return match ? match[1] : null;
};

export function mergeIndexedWrappers(wrappers: any[]): any[] {
    const out: any[] = [];
    const indexByGroup = new Map<string, number>();

    for (const wrapper of wrappers) {
        const base = indexedBaseOf(wrapper);
        if (base) {
            const groupKey = `${wrapper.TargetType ?? ""}|${wrapper.Path ?? ""}|${base}`;
            const idx = indexByGroup.get(groupKey);
            if (idx !== undefined) {
                // Az indexelt kulcsok diszjunktak, a cél-kulcsok pedig azonosak — a szórás nem veszít.
                out[idx] = { ...out[idx], ...wrapper };
                continue;
            }
            indexByGroup.set(groupKey, out.length);
        }
        out.push(wrapper);
    }

    return out;
}

export default mergeIndexedWrappers;
